"use client"

import { useEffect, useState } from "react"
import { ArrowUpRight, ExternalLink, Info, RefreshCw, Search } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { createClient } from "@/lib/supabase/client"
import { readDeepLinkZip } from "@/lib/tools/deep-link"
import { maxCombinedRebateCents } from "@/lib/rebates/rebate-totals"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "cooking-temperature-converter",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

interface RebateResult {
  id: string
  utility_slug: string
  state_code: string
  rebate_category: string
  product_type: string
  rebate_amount_cents: number
  rebate_type: string
  program_name: string | null
  eligible_products: string | null
  claim_url: string | null
  claim_instructions: string | null
  expiration_date: string | null
  source: string | null
}

interface UtilityInfo {
  utility_name: string
  utility_slug: string
  state_code: string
  website_url: string | null
  rebate_page_url: string | null
}

const META = {
  name: "Refrigerator Rebate Finder",
  category: "Energy & Cost" as const,
  mediumDescription:
    "Look up utility rebates and recycling incentives for ENERGY STAR refrigerators and freezers in your ZIP. Pulls live data from 749 utilities across 31,044 ZIP codes.",
  faqs: [
    {
      question: "Where do these rebate amounts come from?",
      answer:
        "Each rebate is verified directly from the utility's published program page before listing. We track 749 utilities across all 50 states and update amounts when programs change.",
    },
    {
      question: "What's the difference between purchase and recycling rebates?",
      answer:
        "Purchase rebates pay you for buying a new ENERGY STAR fridge. Recycling rebates pay you (often $25–$75) to properly retire your old unit. Many utilities offer both, and they can usually be stacked.",
    },
    {
      question: "Why does my ZIP not show any rebates?",
      answer:
        "Some utilities don't run refrigerator-specific rebate programs. Check our state-level rebate index — your state energy office or municipal utility may have separate offers we surface there.",
    },
    {
      question: "Can I combine utility rebates with manufacturer or retailer promos?",
      answer:
        "Almost always yes. Utility rebates are typically separate from manufacturer instant savings and retailer holiday promos — stack those for the lowest net price. Note there is no federal tax credit for refrigerators: the former IRA energy credits (25C and 25D) never covered refrigerators and both ended for property placed in service after December 31, 2025.",
    },
    {
      question: "Are federal tax credits included?",
      answer:
        "There are no federal tax credits for refrigerators. The Inflation Reduction Act's home-energy credits (§25C Energy Efficient Home Improvement Credit and §25D Residential Clean Energy Credit) never covered refrigerators, and both were terminated after December 31, 2025 by the July 2025 budget law (IRS FS-2025-05). The savings we surface are utility-administered rebates, which are unaffected.",
    },
  ],
  relatedTools: ["energy-cost-calculator", "refrigerator-size-calculator", "refrigerator-lifespan-calculator"],
}

export function RefrigeratorRebateFinderPageClient() {
  const [zip, setZip] = useState("")
  const [results, setResults] = useState<RebateResult[]>([])
  const [utility, setUtility] = useState<UtilityInfo | null>(null)
  const [rate, setRate] = useState<number | null>(null)
  const [searched, setSearched] = useState(false)
  const [loading, setLoading] = useState(false)

  // The rebate-finder widget deep-links here as ?zip=<5 digits>. Prefill and
  // run once so the visitor never retypes what the widget already captured;
  // anything that is not a 5-digit ZIP is discarded rather than searched.
  const handleSearch = async (overrideZip?: string) => {
    const zipCode = (overrideZip ?? zip).trim()
    if (zipCode.length !== 5) return
    setLoading(true)
    try {
      const supabase = createClient()

      const { data: coverage } = await supabase
        .from("utility_coverage")
        .select("utility_slug, state_code, city_name")
        .eq("zip_code", zipCode)
        .limit(1)
        .single()

      if (!coverage) {
        setResults([])
        setUtility(null)
        setRate(null)
        setSearched(true)
        setLoading(false)
        return
      }

      const [utilityRes, rebatesRes, rateRes] = await Promise.all([
        supabase
          .from("utilities")
          .select("utility_name, utility_slug, state_code, website_url, rebate_page_url")
          .eq("utility_slug", coverage.utility_slug)
          .single(),
        supabase
          .from("utility_rebates")
          .select("*")
          .eq("utility_slug", coverage.utility_slug)
          .eq("is_active", true),
        supabase.from("zip_energy_data").select("res_rate_kwh").eq("zip", zipCode).single(),
      ])

      if (utilityRes.data) setUtility(utilityRes.data)
      if (rebatesRes.data) setResults(rebatesRes.data)
      if (rateRes.data) setRate(rateRes.data.res_rate_kwh)
    } catch {
      // Silently handle
    } finally {
      setLoading(false)
      setSearched(true)
    }
  }

  // The deep-link search runs inside an async block owned by the effect. The
  // rule accepts setState there and rejects it in the effect body proper, which
  // is the whole of the change — the ?zip= behaviour is identical.
  useEffect(() => {
    const zipCode = readDeepLinkZip(window.location.search)
    if (!zipCode) return
    void (async () => {
      setZip(zipCode)
      await handleSearch(zipCode)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * `overrideZip` exists so the ?zip= deep link can search on mount, before
   * React has committed the state update. Callers in JSX pass nothing.
   */

  // The most one household can stack: alternative tiers for the same
  // appliance are not added together (FRIDGE-1260).
  const totalAmount = maxCombinedRebateCents(results) / 100

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Find every refrigerator rebate"
      heroTitleItalic="in"
      heroTitlePost="your ZIP code."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Verified utility data"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "749", label: "Utilities tracked", note: "All 50 states" },
            { value: "31K", label: "ZIPs covered", note: "EIA-based" },
            { value: "$0", label: "To use", note: "Forever" },
            { value: "Live", label: "Source data", note: "Verified" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-rebate-finder")}
      cta={{
        title: "Stack the rebate with the right ENERGY STAR fridge.",
        body: "Browse certified models and compare live prices across every major retailer to maximize total savings.",
        buttonLabel: "Browse ENERGY STAR fridges",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Five digits.
                <br />
                Real rebates.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We map your ZIP to your local utility, then surface every active refrigerator and freezer rebate they publish.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    ZIP code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      placeholder="e.g., 90210"
                      value={zip}
                      onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      // `min-w-0` lets flex-1 shrink the input below its
                      // intrinsic content min-width — without it the placeholder
                      // forced the card past the mobile grid cell width.
                      // Smaller font on mobile (18 → 22 at sm+) keeps min-content tight.
                      className="flex-1 min-w-0 px-3 sm:px-4 py-3 sm:py-4 text-[18px] sm:text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <button
                      onClick={() => handleSearch()}
                      disabled={zip.length !== 5 || loading}
                      // Tighter padding + shrink-0 on mobile so the icon+label
                      // never compete with the input for shrink budget.
                      className="px-3 sm:px-5 shrink-0 rounded-lg bg-[#ff9951] hover:bg-[#ff8533] disabled:opacity-100 text-[var(--color-brand-action-text)] font-bold inline-flex items-center gap-1.5 sm:gap-2 disabled:cursor-not-allowed"
                    >
                      <Search className="w-4 h-4" />
                      {loading ? "..." : "Find"}
                    </button>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> 31,044 ZIPs supported · EIA residential rate data
                  </div>
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      {searched ? (utility ? utility.utility_name : "No utility match") : "Total available"}
                    </div>
                    {searched && rate && (
                      <div className="text-[11px] uppercase tracking-[0.15em] text-white/60">
                        Rate: {(rate * 100).toFixed(1)}¢/kWh
                      </div>
                    )}
                  </div>
                  <div
                    className="font-black"
                    style={{
                      // Floor was 52px which forced "$1,500 stackable" past the
                      // mobile content column (~280px) and clipped at the right edge.
                      // Lowered floor to 36px, raised vw rate so desktop still hits
                      // the 88px ceiling at the same viewport width.
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {`$${totalAmount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    <span className="text-[16px] sm:text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      stackable
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {!searched && "Enter a ZIP to see what your utility offers."}
                    {searched && results.length === 0 && utility && "Your utility isn't currently running a refrigerator rebate program."}
                    {searched && !utility && "We don't have utility coverage data for that ZIP."}
                    {searched && results.length > 0 &&
                      `${results.length} active rebate${results.length === 1 ? "" : "s"} — see breakdown below.`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rebate listings */}
          {searched && results.length > 0 && (
            <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900">
                  Active rebates from {utility?.utility_name}
                </h3>
                <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                  Verified amounts
                </span>
              </div>
              <div className="space-y-3">
                {results.map((rebate) => (
                  <div key={rebate.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-baseline gap-3">
                          <span className="text-[26px] font-black text-[#ff9951] tracking-tight">
                            {`$${(rebate.rebate_amount_cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-gray-500">
                            {rebate.rebate_type.replace("_", " ")}
                          </span>
                        </div>
                        {rebate.program_name && (
                          <div className="font-bold text-gray-900 mt-1 text-[14px]">{rebate.program_name}</div>
                        )}
                        <div className="text-[12px] text-gray-600 mt-1">
                          {rebate.product_type === "both"
                            ? "Refrigerators & freezers"
                            : rebate.product_type === "refrigerator"
                            ? "Refrigerators"
                            : "Freezers"}
                          {rebate.rebate_category === "recycling" ? " · recycling" : " · purchase"}
                        </div>
                        {rebate.eligible_products && (
                          <div className="text-[12px] text-gray-500 mt-1.5">
                            <span className="font-semibold">Eligible:</span> {rebate.eligible_products}
                          </div>
                        )}
                      </div>
                      {rebate.claim_url && (
                        <a
                          href={rebate.claim_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[12px] font-bold text-[#0469f0] hover:underline"
                        >
                          Claim <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the rebate"
          title="Counter-depth fridges with rebate-eligible specs"
          description="Certified models with the lowest annual kWh — eligible for nearly every utility rebate program."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Verified rebates. <em className="italic text-[#ff9951]">Real</em> sources.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Coverage data comes from EIA Form-861 utility filings and the U.S. Census ZIP-to-utility crosswalk. Rebate amounts are scraped from each utility&apos;s published program page.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "ZIP → utility map",
                  d: "31,044 ZIPs mapped to 749 utilities using EIA Form-861 service-territory filings.",
                },
                {
                  n: "02",
                  t: "Rebate verification",
                  d: "Every dollar amount is verified against the utility's official program page before listing.",
                },
                {
                  n: "03",
                  t: "Live electricity rate",
                  d: "Residential ¢/kWh comes from EIA-tracked utility filings, joined to your ZIP for context.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges — lowest baseline cost"
          description="The cheapest refrigerator style to run, year over year — often eligible for additional utility incentives."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
